import * as React from 'react';
import bind from '@reverbdotcom/commons/dist/bind';
import * as I18n from 'i18n-js';
import * as elog from '@reverbdotcom/commons/dist/elog';
import { ITokenizedCreditCardData } from './card_data';
import {
  mountAdyenSecuredFields,
  ITokenizedFieldState,
  IOnFieldValidData,
  IOnFocusData,
  IOnErrorData,
} from './adyen_secured_fields';
import TokenizedField from './tokenized_field';
import FormGroup from '@reverbdotcom/commons/dist/components/form_group';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/dist/components/user_context_provider';

interface ExternalProps {
  onTokenizedFieldUpdate(attributes: Partial<ITokenizedCreditCardData>): void;
  creditCardId: string;
  errorReverifying: boolean;
  disableSubmit: boolean;
  onSubmit: (event: any) => void;
  waitForConfigSuccessSeconds?: number;
}

export type IProps = ExternalProps & IUserContext;

interface IState {
  encryptedSecurityCode: ITokenizedFieldState;
  adyenConfigFinished: boolean;
  timer: ReturnType<typeof setTimeout>;
}

const REVERIFY_CARD_CONTAINER_PREFIX = 'reverify-card-container';
const COMPONENT_NAME = 'TokenizedCvvField';

export class TokenizedCvvField extends React.Component<IProps, IState> {
  static defaultProps: Partial<IProps> = {
    waitForConfigSuccessSeconds: 30,
  };

  constructor(props) {
    super(props);

    this.state = {
      timer: null,
      adyenConfigFinished: false,
      encryptedSecurityCode: {
        hasFocus: false,
        error: '',
      },
    };
  }

  @bind
  onAdyenConfigSuccess() {
    this.setState({ adyenConfigFinished: true });
  }

  @bind
  checkIfConfigFinished() {
    if (!this.state.adyenConfigFinished) {
      const message = `Adyen CVV re-verification form failed to initialize after ${this.props.waitForConfigSuccessSeconds}s. If this is happening frequently, deactivate rollout: "adyen_checkout_api_vaulting"`;
      elog.error(
        'adyen_cvv_form_failed_to_initialize',
        { message, componentName: COMPONENT_NAME },
        null,
      );
    }
  }

  secondsToMilliseconds(seconds: number) {
    return seconds * 1000;
  }

  componentDidMount() {
    this.setState({
      timer: setTimeout(
        this.checkIfConfigFinished,
        this.secondsToMilliseconds(this.props.waitForConfigSuccessSeconds),
      ),
    });
    mountAdyenSecuredFields({
      domSelector: `#${this.containerId()}`,
      locale: this.props.user.locale,
      onFieldValid: this.onFieldValid,
      onConfigSuccess: this.onAdyenConfigSuccess,
      onFocus: this.onFocus,
      onError: this.onError,
    });
  }

  componentWillUnmount() {
    if (this.state.timer) {
      clearTimeout(this.state.timer);
    }
  }

  @bind
  onFieldValid(data: IOnFieldValidData) {
    const tokenizedData: Partial<ITokenizedCreditCardData> = {
      [data.encryptedFieldName]: data.blob || '',
    };

    this.props.onTokenizedFieldUpdate(tokenizedData);
  }

  @bind
  onFocus(data: IOnFocusData) {
    const fieldState = this.state[data.fieldType];
    const newFieldState: ITokenizedFieldState = { ...fieldState, hasFocus: data.focus };
    const newState = {
      [data.fieldType]: newFieldState,
    } as Pick<IState, 'encryptedSecurityCode'>;

    this.setState(newState);
  }

  @bind
  onError(data: IOnErrorData) {
    const fieldState = this.state[data.fieldType];
    const errorValue = data.error === '' ? '' : I18n.t(`discovery.creditCardForm.${data.fieldType}Error`);
    const newFieldState: ITokenizedFieldState = { ...fieldState, error: errorValue };
    const newState = {
      [data.fieldType]: newFieldState,
    } as Pick<IState, 'encryptedSecurityCode'>;

    this.setState(newState);
  }

  @bind
  containerId(): string {
    return `${REVERIFY_CARD_CONTAINER_PREFIX}-${this.props.creditCardId}`;
  }

  hasError() {
    return (
      this.state.encryptedSecurityCode.error !== '' ||
        this.props.errorReverifying
    );
  }

  errorMessage() {
    if (!this.hasError()) { return null; }
    return I18n.t('discovery.creditCardForm.encryptedSecurityCodeError');
  }

  renderTokenizedField() {
    const hasFocus = this.state.encryptedSecurityCode.hasFocus;

    return (
      <TokenizedField
        fieldName="encryptedSecurityCode"
        hasFocus={hasFocus}
        hasError={this.hasError()}
      />
    );
  }

  render() {
    return (
      <div className="cc-verify-cvv" id={this.containerId()}>
        <FormGroup
          label={I18n.t('discovery.creditCardForm.pleaseReverifyCvv')}
          inputName="encryptedSecurityCode"
          helpText={I18n.t('discovery.creditCardForm.verifyCvvForAdditionalSecurity')}
          errorText={this.errorMessage()}
        >
          <p className="form-group__help-text mb-1">
            {I18n.t('discovery.creditCardForm.cvvLocation')}
          </p>
          <div className="d-flex fx-align-start">
            <div className="width-rem-10 mr-1">
              {this.renderTokenizedField()}
            </div>
            <form
              id={`form-${this.containerId()}`}
            >
              <button
                className="button button--blue lh-180 button--small"
                type="button"
                onClick={this.props.onSubmit}
                disabled={this.props.disableSubmit}
              >
                {I18n.t('discovery.creditCardForm.verifyCvvButton')}
              </button>
            </form>
          </div>
        </FormGroup>
      </div>
    );
  }
}

export default withUserContext(TokenizedCvvField);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/credit_cards/tokenized_cvv_field.tsx